import React, {useState} from "react";
import {ColumnDescription} from "react-bootstrap-table-next";
import {SortOrder} from "../../../shared/enums/sortOrder";
import classNames from "classnames";
import PaginatedDataTable from "../../molecules/paginatedDataTable/paginatedDataTable";
import {useTranslation} from "react-i18next";
import {observer} from "mobx-react-lite";
import {useStores} from "../../../stores/useStores";
import {format} from "date-fns";
import Tag from "../../atoms/tag/tag";
import Icon from "../../atoms/common/icon/icon";
import {IconSource} from "../../../shared/enums/iconSource";
import ProcessedBookingGroup from "../../../stores/domainStore/pages/models/processedBookingGroup";
import ProcessedBooking from "../../../stores/domainStore/pages/models/processedBooking";
import {TransactionsColumnNames} from "../../../shared/enums/transactionsColumnNames";
import {TimeZones} from "../../../shared/enums/timeZones";
import {utcToZonedTime} from "date-fns-tz";
import {TransactionStatus} from "../../../shared/enums/transactionStatus";
import "./processedBookingItem.sass";
import TransactionItemTitle from "../../molecules/transactionEventTitle/transactionItemTitle";
import {getEventsUrl} from "../../../utils/urlUtils";
import {QueryParams} from "../../../shared/enums/queryParams";
import {Link} from "react-router-dom";
import {ProcessedBookingsSortUtil} from "../../../utils/sortUtils/processedBookingsSortUtil";
import {Collapse} from "reactstrap";

export interface IProcessedBookingItemProps {
  bookingGroup: ProcessedBookingGroup;
}

const ProcessedBookingItem: React.FC<IProcessedBookingItemProps> = ({
  bookingGroup
}) => {
  const {t} = useTranslation(["translation", "event", "eventTemplate"]);
  const {
    uiStore: {transactionsUiStore},
    userService
  } = useStores();

  const id = bookingGroup.id;
  const isExpanded = transactionsUiStore.expandedEventId === id;

  const selectedVillage = userService?.getSelectedVillage();
  const timezone = selectedVillage?.timeZone ?? TimeZones.Auckland;

  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.Ascending);
  const [sortField, setSortField] = useState<TransactionsColumnNames>(
    TransactionsColumnNames.DateCharged
  );

  const sortedTransactions = new ProcessedBookingsSortUtil(
    bookingGroup.processedBookings,
    sortField,
    sortOrder
  ).sort();

  const headerLabelFormatter = (column: ColumnDescription<any, any>) => {
    const isColumnSorting = sortField === column.dataField;
    const isSortAscending = sortOrder === SortOrder.Ascending;
    return (
      <div
        className={classNames("processed-bookings-event-sortable-header", {
          "is-sort": isColumnSorting
        })}
      >
        {column.text}
        {column.sort && (
          <div>
            {isColumnSorting && !isSortAscending && (
              <Icon source={IconSource.toggleUp} />
            )}
            {isColumnSorting && isSortAscending && (
              <Icon source={IconSource.toggleDown} />
            )}
            {!isColumnSorting && <Icon source={IconSource.toggleBoth} />}
          </div>
        )}
      </div>
    );
  };

  const dateChargedFormatter = (
    _: any,
    template: ProcessedBooking,
    __: any
  ) => {
    return format(utcToZonedTime(template.createdDate, timezone), "dd/MM/yyyy");
  };

  const chargedToNameFormatter = (
    _: any,
    template: ProcessedBooking,
    __: any
  ) => {
    return template.payeeName;
  };

  const villageIdQueryParam = `${QueryParams.VillageId}=${selectedVillage?.id}`;

  const descriptionFormatter = (_: any, booking: ProcessedBooking) => {
    const toEventButtonTitle = bookingGroup.eventTitle;
    const descriptionElements =
      booking.transactionDetails.split(toEventButtonTitle);
    const hasEventTitle =
      booking.transactionDetails.includes(toEventButtonTitle);

    const href = getEventsUrl(
      booking.eventId,
      villageIdQueryParam,
      "Independent Residents",
      t("event:titles.viewEvent"),
      booking.eventOccurrenceDate
    );

    const eventLink = (
      <Link to={href} data-testid="event-link">
        {hasEventTitle ? toEventButtonTitle : ` - ${toEventButtonTitle}`}
      </Link>
    );

    return (
      <span>
        {descriptionElements[0]}
        {eventLink}
        {descriptionElements.length > 1 ? descriptionElements[1] : null}
      </span>
    );
  };

  const eventTemplateStatusFormatter = (_: any, template: ProcessedBooking) => {
    if (template.isSuccess) {
      return (
        <Tag type="success" text={TransactionStatus.Confirmed.toString()} />
      );
    }

    return (
      <Tag
        type="negative"
        children={
          <>
            <Icon source={IconSource.error} />
            {TransactionStatus.Error.toString()}
          </>
        }
      />
    );
  };

  const columns: ColumnDescription<any, any>[] = [
    {
      dataField: "id",
      hidden: true,
      text: "Id"
    },
    {
      dataField: TransactionsColumnNames.DateCharged,
      text: t("event:labels.transactionsTable.dateCharged"),
      headerClasses: "",
      classes: "date-charged-col",
      sort: true,
      headerFormatter: headerLabelFormatter,
      formatter: dateChargedFormatter,
      formatExtraData: isExpanded
    },
    {
      dataField: TransactionsColumnNames.ChargedTo,
      text: t("event:labels.transactionsTable.chargedTo"),
      headerClasses: "",
      classes: "charged-to-col",
      sort: true,
      headerFormatter: headerLabelFormatter,
      formatter: chargedToNameFormatter,
      formatExtraData: isExpanded
    },
    {
      dataField: TransactionsColumnNames.Description,
      text: t("event:labels.transactionsTable.description"),
      headerClasses: "",
      classes: "description-col",
      sort: true,
      headerFormatter: headerLabelFormatter,
      formatter: descriptionFormatter,
      formatExtraData: isExpanded
    },
    {
      dataField: TransactionsColumnNames.Status,
      text: t("event:labels.transactionsTable.status"),
      headerClasses: "",
      classes: "status-col",
      sort: true,
      headerFormatter: headerLabelFormatter,
      formatter: eventTemplateStatusFormatter
    }
  ];

  const sortHandler = (sortField: string, sortOrder: SortOrder): void => {
    const sortColumn = sortField as TransactionsColumnNames;
    setSortField(sortColumn);
    setSortOrder(sortOrder);
  };

  return (
    <div
      className="processed-booking-item card"
      data-testid="processedBookingItem"
    >
      <div className="header" id={"heading-" + id}>
        <h2 className="mb-0">
          <button
            className="btn collapsed"
            type="button"
            data-toggle="collapse"
            data-target={"#collapse-" + id}
            aria-expanded={isExpanded}
            aria-controls={"collapse-" + id}
            onClick={() => transactionsUiStore.setExpandedEventId(id)}
          >
            <TransactionItemTitle
              processedBookingGroup={bookingGroup}
              isExpanded={isExpanded}
            />
            {isExpanded ? (
              <Icon source={IconSource.angleUp} />
            ) : (
              <Icon source={IconSource.dropdown} />
            )}
          </button>
        </h2>
      </div>

      <Collapse
        isOpen={isExpanded}
        id={"collapse-" + id}
        className={classNames("expandable")}
        aria-labelledby={"heading-" + id}
        data-parent="#eventTemplateCategoriesList"
      >
        <PaginatedDataTable
          data={sortedTransactions}
          keyField="id"
          columns={columns}
          sizePerPage={bookingGroup.processedBookingsCount}
          dataLength={bookingGroup.processedBookingsCount}
          onSort={sortHandler}
          remote={true}
          noBorderBottom={true}
        />
      </Collapse>
    </div>
  );
};
export default observer(ProcessedBookingItem);
